import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { 
	Loader2, 
	Users, 
	Clock, 
	CheckCircle2,
	AlertCircle,
	Sparkles
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { toast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { useTranslation } from '@/hooks/useTranslation'

interface AgentProfile {
	user_id: string
	full_name: string
	job_title?: string
	bio?: string
	avatar_url?: string
	languages?: string[]
	status?: string
	status_message?: string
	greeting_message?: string
	expertise_areas?: string[]
	response_time_minutes?: number
	is_active?: boolean
}

interface TeamAgentsViewProps {
	programId?: string
	showEditControls?: boolean
}

export default function TeamAgentsView({
	programId,
	showEditControls = false,
}: TeamAgentsViewProps) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const [editingAgent, setEditingAgent] = useState<string | null>(null)

	// Fetch program agents
	const { data: agentsData, isLoading, error } = useQuery({
		queryKey: ['program-agents', programId],
		queryFn: async () => {
			if (!programId) {
				return {
					agents: [],
					program: null
				}
			}
			const response = await apiClient(`/api/agent-profile/program/${programId}/all`)
			return response as {
				agents: AgentProfile[]
				program?: {
					id: string
					name: string
					team_id: string
				}
			}
		},
		enabled: !!programId,
	})

	const agents = agentsData?.agents || []
	const onlineAgents = agents.filter(a => a.status === 'online' && a.is_active)
	const awayAgents = agents.filter(a => a.status === 'away' && a.is_active)
	const offlineAgents = agents.filter(a => (a.status === 'offline' || !a.is_active))
	
	// Check if we have real response time data
	const hasRealResponseTimeData = agents.some(agent => agent.response_time_minutes && agent.response_time_minutes !== 5)
	
	// Calculate average response time only if we have real data
	const avgResponseTime = hasRealResponseTimeData && agents.length > 0
		? Math.round(agents.filter(a => a.response_time_minutes).reduce((acc, agent) => acc + (agent.response_time_minutes || 0), 0) / agents.filter(a => a.response_time_minutes).length)
		: null

	// Toggle agent active status
	const toggleAgentMutation = useMutation({
		mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
			const agent = agents.find(a => a.user_id === userId)
			if (!agent) throw new Error('Agent not found')

			return apiClient(`/api/agent-profile/admin/${userId}?program_id=${programId}`, {
				method: 'PUT',
				body: JSON.stringify({
					profileData: {
						...agent,
						is_active: isActive,
					}
				})
			})
		},
		onSuccess: (_, { userId, isActive }) => {
			const agent = agents.find(a => a.user_id === userId)
			queryClient.invalidateQueries({ queryKey: ['program-agents', programId] })
			toast({
				title: t('teamAgentsView.success'),
				description: t('teamAgentsView.agentIsNow', { 
					name: agent?.full_name || '', 
					status: isActive ? t('teamAgentsView.active') : t('teamAgentsView.inactive') 
				}),
			})
		},
		onError: () => {
			toast({
				title: t('teamAgentsView.error'),
				description: t('teamAgentsView.failedToUpdateAgentStatus'),
				variant: 'destructive',
			})
		},
	})

	// Handle no program selected
	if (!programId) {
		return (
			<div className="flex flex-col items-center justify-center py-12 px-4">
				<Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
				<h2 className="text-lg font-semibold text-foreground mb-2">
					{t('teamAgentsView.noProgramSelected')}
				</h2>
				<p className="text-sm text-muted-foreground text-center max-w-sm">
					{t('teamAgentsView.pleaseSelectProgram')}
				</p>
			</div>
		)
	}

	// Loading state
	if (isLoading) {
		return (
			<div className="flex flex-col items-center justify-center py-12 px-4">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
				<p className="text-sm text-muted-foreground">{t('teamAgentsView.loadingTeamAgents')}</p>
			</div>
		)
	}

	// Error state
	if (error) {
		return (
			<div className="flex flex-col items-center justify-center py-12 px-4">
				<AlertCircle className="h-12 w-12 text-destructive/50 mb-4" />
				<h2 className="text-lg font-semibold text-foreground mb-2">
					{t('teamAgentsView.somethingWentWrong')}
				</h2>
				<p className="text-sm text-muted-foreground text-center max-w-sm mb-4">
					{t('teamAgentsView.couldntLoadTeamAgents')}
				</p>
				<Button
					variant="outline"
					onClick={() => queryClient.invalidateQueries({
						queryKey: ['program-agents', programId]
					})}>
					{t('teamAgentsView.tryAgain')}
				</Button>
			</div>
		)
	}

	// Empty state
	if (agents.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12 px-4">
				<div className="relative mb-4">
					<Users className="h-16 w-16 text-muted-foreground/30" />
					<Sparkles className="h-6 w-6 text-primary absolute -top-1 -right-1" />
				</div>
				<h2 className="text-lg font-semibold text-foreground mb-2">
					{t('teamAgentsView.noAgentsYet')}
				</h2>
				<p className="text-sm text-muted-foreground text-center max-w-sm">
					{t('teamAgentsView.agentsWillAppearHere')}
				</p>
			</div>
		)
	}

	return (
		<div className="space-y-8">
			{/* Team Overview Cards */}
			<div className="grid gap-4 md:grid-cols-2">
				<div className="rounded-xl border bg-gradient-to-br from-emerald-50 to-white p-4">
					<div className="flex items-start justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">{t('teamAgentsView.onlineNow')}</p>
							<p className="text-2xl font-semibold text-emerald-700 mt-1">
								{onlineAgents.length}
							</p>
						</div>
						<div className="p-2 bg-emerald-100 rounded-lg">
							<CheckCircle2 className="h-4 w-4 text-emerald-600" />
						</div>
					</div>
					<div className="flex gap-1 mt-3">
						{onlineAgents.slice(0, 3).map((agent) => (
							<div key={agent.user_id} className="h-2 w-2 rounded-full bg-emerald-500" />
						))}
						{onlineAgents.length > 3 && (
							<span className="text-xs text-emerald-600 ml-1">+{onlineAgents.length - 3}</span>
						)}
					</div>
				</div>

				<div className="rounded-xl border bg-gradient-to-br from-amber-50 to-white p-4">
					<div className="flex items-start justify-between">
						<div>
							<p className="text-sm font-medium text-muted-foreground">{t('teamAgentsView.away')}</p>
							<p className="text-2xl font-semibold text-amber-700 mt-1">
								{awayAgents.length}
							</p>
						</div>
						<div className="p-2 bg-amber-100 rounded-lg">
							<Clock className="h-4 w-4 text-amber-600" />
						</div>
					</div>
					<p className="text-xs text-muted-foreground mt-3">
						{t('teamAgentsView.willReturnSoon')}
					</p>
				</div>
			</div>

			{/* Agents Grid */}
			<div>
				<div className="mb-6">
					<h2 className="text-lg font-semibold text-foreground">
						{t('teamAgentsView.activeAgents')}
					</h2>
					<p className="text-sm text-muted-foreground">
						{t('teamAgentsView.agentsConfigured', { count: agents.length })}
					</p>
				</div>

				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{agents.map((agent) => (
						<div
							key={agent.user_id}
							className={cn(
								"group relative rounded-xl border bg-card p-6 transition-all duration-200",
								agent.is_active 
									? "hover:shadow-md hover:border-primary/20" 
									: "opacity-60"
							)}>
							{/* Status Badge */}
							<div className="absolute top-4 right-4">
								<div className={cn(
									"flex items-center gap-1.5 rounded-full px-2 py-1",
									agent.status === 'online' && agent.is_active && "bg-emerald-100",
									agent.status === 'away' && agent.is_active && "bg-amber-100",
									(!agent.is_active || agent.status === 'offline') && "bg-gray-100"
								)}>
									<div className={cn(
										"h-2 w-2 rounded-full",
										agent.status === 'online' && agent.is_active && "bg-emerald-500",
										agent.status === 'away' && agent.is_active && "bg-amber-500",
										(!agent.is_active || agent.status === 'offline') && "bg-gray-400"
									)} />
									<span className={cn(
										"text-xs font-medium",
										agent.status === 'online' && agent.is_active && "text-emerald-700",
										agent.status === 'away' && agent.is_active && "text-amber-700",
										(!agent.is_active || agent.status === 'offline') && "text-gray-600"
									)}>
										{!agent.is_active 
											? t('teamAgentsView.inactive') 
											: agent.status === 'online' 
												? t('teamAgentsView.online') 
												: agent.status === 'away'
													? t('teamAgentsView.away')
													: t('teamAgentsView.offline')
										}
									</span>
								</div>
							</div>

							{/* Agent Info */}
							<div className="flex flex-col items-center text-center space-y-3">
								<Avatar className="h-20 w-20 ring-4 ring-white shadow-lg">
									{agent.avatar_url && (
										<AvatarImage
											src={agent.avatar_url}
											alt={agent.full_name}
											className="object-cover"
										/>
									)}
									<AvatarFallback className="text-lg font-medium bg-primary/10 text-primary">
										{agent.full_name
											.split(' ')
											.filter((n) => n)
											.map((n) => n[0])
											.join('')
											.toUpperCase() || 'U'}
									</AvatarFallback>
								</Avatar>

								<div>
									<h3 className="font-semibold text-foreground">
										{agent.full_name}
									</h3>
									<p className="text-sm text-muted-foreground">
										{agent.job_title || t('teamAgentsView.teamMember')}
									</p>
								</div>

								{agent.status === 'away' && agent.status_message && (
									<p className="text-xs text-muted-foreground italic">
										"{agent.status_message}"
									</p>
								)}
							</div>

							{/* Actions */}
							{showEditControls && (
								<div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t">
									<div className="flex items-center gap-2">
										<Switch
											checked={agent.is_active}
											onCheckedChange={(checked) => 
												toggleAgentMutation.mutate({ 
													userId: agent.user_id, 
													isActive: checked 
												})
											}
											className="data-[state=checked]:bg-primary"
										/>
										<label className="text-sm text-muted-foreground">
											{t('teamAgentsView.active')}
										</label>
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			</div>
		</div>
	)
}